import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQuery, axiosQuery } from "./basequery";
import { Environment, getEnvironment } from "utils/getEnvironment";
import appConfig from "configs/app.config";

export const customerServiceApi = createApi({
  reducerPath: "customerServiceApi",
  baseQuery: axiosQuery({
    baseUrl:
      getEnvironment(Environment.DEVELOPMENT)
        ? `${appConfig.apiHostStaging}customer-service/api/v1/`
        : `${appConfig.apiHostPrefix}customer-service/api/v1/`,
  }),
  tagTypes: [
    "customerServices",
    "customerService",
    "connect",
    "connectInsta",
    "connectEmail",
    "connectCall",
    "connectWhatsApp",
  ],
  endpoints: (build) => ({
    createCustomerService: build.mutation({
      query: ({ ...values }) => ({
        url: `organizations`,
        method: "POST",
        data: { ...values },
      }),
      invalidatesTags: ["customerServices"],
    }),

    deleteCustomerService: build.mutation({
      query: ({ organizationId }) => ({
        url: `organizations/${organizationId}`,
        method: "delete",
      }),
      invalidatesTags: ["customerServices"],
    }),

    editCustomerService: build.mutation({
      query: ({ body, organizationId }) => ({
        url: `organizations/${organizationId}`,
        method: "put",
        data: body,
      }),
      invalidatesTags: ["customerService"],
    }),

    getAllCustomerServices: build.query({
      query: () => ({
        url: `organizations`,
      }),
      providesTags: (data) =>
        data
          ? [
              ...data?.map?.(({ id }) => ({ type: "customerService", id })),
              { type: "customerService", id: "LIST" },
            ]
          : [{ type: "customerService", id: "LIST" }],
    }),

    getCustomerService: build.query({
      query: ({ id }) => ({
        url: `organizations/${id}`,
      }),
      providesTags: ["customerService"],
    }),

    getCustomerServiceLogs: build.query({
      query: ({ id, type = "none", offset, limit }) => ({
        url: `${id}/logs?type=${type}&offset=${offset}`,
      }),
    }),

    getRegisteredFBPages: build.query({
      query: ({ organizationId }) => ({
        url: `${organizationId}/fb/pages/`,
      }),
      providesTags: (data) =>
        data
          ? [
              ...data?.map?.(({ id }) => ({ type: "connect", id })),
              { type: "connect", id: "LIST" },
            ]
          : [{ type: "connect", id: "LIST" }],
    }),

    getRegisteredFBPage: build.query({
      query: ({ organizationId, pageId }) => ({
        url: `${organizationId}/fb/pages/${pageId}`,
      }),
    }),

    reAuthorizeFacebook: build.mutation({
      query: ({ accessToken }) => ({
        url: `fb/access-token`,
        method: "put",
        data: { fb_x_token: accessToken },
      }),
    }),

    getRegisteredInstagramPages: build.query({
      query: ({ organizationId }) => ({
        url: `${organizationId}/insta/pages/`,
      }),
      providesTags: (data) =>
        data
          ? [
              ...data?.map?.(({ id }) => ({ type: "connectInsta", id })),
              { type: "connectInsta", id: "LIST" },
            ]
          : [{ type: "connectInsta", id: "LIST" }],
    }),

    getRegisteredInstagramPage: build.query({
      query: ({ organizationId, pageId }) => ({
        url: `${organizationId}/insta/pages/${pageId}`,
      }),
    }),

    getUsersFBPages: build.query({
      query: ({ accessToken, ...params }) => ({
        baseUri: `https://graph.facebook.com/v18.0/me/accounts`,
        params: { access_token: accessToken, ...params },
      }),
    }),

    deleteFBPage: build.mutation({
      query: ({ pageId }) => ({
        url: `facebook/${pageId}`,
        method: "delete",
      }),
      invalidatesTags: ["connect"],
    }),

    getUsersInstagramPages: build.query({
      query: () => ({
        url: ``,
      }),
    }),

    deleteInstagramPage: build.mutation({
      query: ({ instagramId }) => ({
        url: `instagram/${instagramId}`,
        method: "delete",
      }),
    }),

    createFBConnection: build.mutation({
      query: ({ body }) => ({
        url: `fb/connect`,
        method: "post",
        data: body,
      }),
      invalidatesTags: ["connect"],
    }),

    createInstaConnection: build.mutation({
      query: ({ body }) => ({
        url: `insta/connect`,
        method: "post",
        data: body,
      }),
      invalidatesTags: ["connectInsta"],
    }),

    createEmailConnection: build.mutation({
      query: ({ organization_id, chatbot_id, code }) => ({
        url: `outlook/mails/connect`,
        method: "post",
        data: {
          organization_id,
          chatbot_id,
          code,
        },
      }),
      invalidatesTags: ["connectEmail"],
    }),

    getConnectedEmails: build.query({
      query: ({ organization_id }) => ({
        url: `${organization_id}/outlook/mails`,
      }),
      providesTags: (data) =>
        data
          ? [
              ...data?.map?.(({ id }) => ({ type: "connectEmail", id })),
              { type: "connectEmail", id: "LIST" },
            ]
          : [{ type: "connectEmail", id: "LIST" }],
    }),

    getConnectedEmail: build.query({
      query: ({ mail_id }) => ({
        url: `outlook/mails/${mail_id}`,
      }),
    }),

    changeEmailChatbot: build.mutation({
      query: ({ mailId, chatbot_id }) => ({
        url: `mail/${mailId}`,
        method: "put",
        data: { chatbot_id },
      }),
    }),

    deleteEmail: build.mutation({
      query: ({ mailId }) => ({
        url: `outlook/mails/${mailId}`,
        method: "delete",
      }),
      invalidatesTags: ["connectEmail"],
    }),

    getConnectedPhoneCalls: build.query({
      query: ({ organizationId }) => ({
        url: `${organizationId}/connected/calls`,
      }),
      providesTags: (data) =>
        data
          ? [
              ...data?.map?.(({ id }) => ({ type: "connectCall", id })),
              { type: "connectCall", id: "LIST" },
            ]
          : [{ type: "connectCall", id: "LIST" }],
    }),

    getPhoneCallSessions: build.query({
      query: ({ phoneId }) => ({
        url: `calls/${phoneId}/sessions`,
      }),
    }),

    getPhoneCallSession: build.query({
      query: ({ phoneId, sessionId }) => ({
        url: `calls/${phoneId}/sessions/${sessionId}`,
      }),
    }),

    deletePhoneCall: build.mutation({
      query: ({ callId }) => ({
        url: `call/${callId}`,
        method: "delete",
      }),
      invalidatesTags: ["connectCall"],
    }),

    createRequest: build.mutation({
      query: ({ ...values }) => ({
        url: `calls/requests`,
        method: "post",
        data: { ...values },
      }),
      invalidatesTags: ["connectCall"],
    }),

    createWhatsAppConnect: build.mutation({
      query: ({ ...values }) => ({
        url: `whatsapp/connect`,
        method: "Post",
        data: { ...values },
      }),
    }),

    changeWhatsAppChatbot: build.mutation({
      query: ({ whatsappId, chatbot_id }) => ({
        url: `whatsapp/${whatsappId}`,
        method: "put",
        data: { chatbot_id },
      }),
    }),

    reAuthorizeWhatsApp: build.mutation({
      query: ({ whatsapp_id }) => ({
        url: `whatsapp/${whatsapp_id}/reconnect`,
        method: "Post",
        data: {},
      }),
      invalidatesTags: ["connectWhatsApp"],
    }),

    getConnectedWhatsApps: build.query({
      query: ({ organizationId }) => ({
        url: `${organizationId}/connected/whatsapp`,
      }),
      providesTags: (data) =>
        data
          ? [
              ...data?.map?.(({ id }) => ({ type: "connectWhatsApp", id })),
              { type: "connectWhatsApp", id: "LIST" },
            ]
          : [{ type: "connectWhatsApp", id: "LIST" }],
    }),

    getConnectedWhatsApp: build.query({
      query: ({ whatsAppId }) => ({
        url: `whatsapp/${whatsAppId}`,
      }),
    }),

    deleteWhatsApp: build.mutation({
      query: ({ whatsappId }) => ({
        url: `whatsapp/${whatsappId}`,
        method: "delete",
      }),
    }),
  }),
});

export const {
  useCreateCustomerServiceMutation,
  useGetAllCustomerServicesQuery,
  useGetCustomerServiceQuery,
  useGetRegisteredFBPagesQuery,
  useGetUsersFBPagesQuery,
  useGetCustomerServiceLogsQuery,
  useGetRegisteredFBPageQuery,
  useCreateFBConnectionMutation,
  useGetRegisteredInstagramPageQuery,
  useGetRegisteredInstagramPagesQuery,
  useCreateInstaConnectionMutation,
  useCreateEmailConnectionMutation,
  useGetConnectedEmailQuery,
  useGetConnectedEmailsQuery,
  useGetConnectedPhoneCallsQuery,
  useGetPhoneCallSessionsQuery,
  useGetPhoneCallSessionQuery,
  useCreateRequestMutation,
  useGetConnectedWhatsAppQuery,
  useGetConnectedWhatsAppsQuery,
  useCreateWhatsAppConnectMutation,
  useReAuthorizeWhatsAppMutation,
  useChangeWhatsAppChatbotMutation,
  useChangeEmailChatbotMutation,
  useDeleteCustomerServiceMutation,
  useDeleteInstagramPageMutation,
  useDeletePhoneCallMutation,
  useDeleteEmailMutation,
  useDeleteWhatsAppMutation,
  useDeleteFBPageMutation,
  useEditCustomerServiceMutation,
  useReAuthorizeFacebookMutation,
} = customerServiceApi;
